import React from "react";
import { ImagePlus, Trash2 } from "lucide-react";
import { Button } from "@pgs-core/ui/components/button";
import { useMediaPicker } from "./media-picker-dialog";
import type { MediaItem } from "../lib/media";
import type { ApiClient } from "../lib/api";

export interface ImageFieldValue {
  key: string;
  /** Preview URL when known; older stored keys may not have one resolved. */
  url?: string | null;
}

/**
 * The standard "image option" control: a preview plus Choose/Replace (opens
 * the Media picker — browse the library or upload). The preview is a fixed,
 * self-contained rounded thumbnail (own `overflow-hidden` so it never inherits
 * a host's border-radius, e.g. the section canvas's site tokens); hovering it
 * reveals a trash overlay that removes the image. Use this anywhere a single
 * image can be set.
 */
export function ImageField({
  api,
  value,
  onChange,
  emptyLabel = "No image",
  chooseLabel = "Choose image",
  replaceLabel = "Replace image",
}: {
  api: ApiClient | null;
  value: ImageFieldValue | null;
  onChange: (item: MediaItem | null) => void;
  emptyLabel?: string;
  chooseLabel?: string;
  replaceLabel?: string;
}) {
  const { pickImage, pickerElement } = useMediaPicker(api);

  const choose = async () => {
    const item = await pickImage();
    if (item) onChange(item);
  };

  return (
    <div className="flex items-center gap-3">
      {value?.url ? (
        <div className="group/thumb relative h-12 w-20 shrink-0 overflow-hidden rounded-md border">
          <img src={value.url} alt="" className="h-full w-full object-cover" />
          <button
            type="button"
            onClick={() => onChange(null)}
            aria-label="Remove image"
            className="absolute inset-0 flex items-center justify-center bg-black/55 text-white opacity-0 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ) : value?.key ? (
        <span className="max-w-40 truncate text-xs text-muted-foreground" title={value.key}>
          Image set (shown on the live site)
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">{emptyLabel}</span>
      )}
      <Button type="button" variant="outline" size="sm" onClick={() => { void choose(); }}>
        <ImagePlus className="h-4 w-4" />
        {value?.key ? replaceLabel : chooseLabel}
      </Button>
      {/* No preview to hover (stored key without a resolved URL this session):
          keep an explicit remove control. */}
      {value?.key && !value?.url && (
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)} aria-label="Remove image">
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
      {pickerElement}
    </div>
  );
}
